import { Accordion, Anchor, Button, Checkbox, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, ReactNode, redirect, useNavigate } from "@tanstack/react-router";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content, PaperWrapper } from "shared/components/global/Content";
import { EmailEuds, TextEuds, VoiceEuds } from "shared/components/settings/euds";
import { GroupsContainer } from "shared/components/settings/groups";
import { LocationsContainer } from "shared/components/settings/locations";
import { ProfileContainer } from "shared/components/settings/profile";
import { TopicsContainer } from "shared/components/settings/TopicsContainer";
import { features } from "shared/graphql/features";
import { userQueryOptions, useUser } from "shared/graphql/users";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { isSupport } from "shared/hooks/useIsSupport";
import { getUserId } from "shared/stores/oidc";
import { to } from "shared/utils/fns";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { UserStatus } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { z } from "zod";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const registerFormStateSchema = z.object({
    didConsentToPrivacyPolicy: z.boolean(),
    didConsentToEula: z.boolean(),
});

const Page = () => {
    features.useLoader();
    const nav = useNavigate();
    const hasTopics = features.enabled("HAS_TOPICS");

    const { user, refetch } = useUser({ assert: true });
    const { data: customerData } = useSuspenseQuery(queries.customer);
    const register = useGraphqlMutation({ document: mutations.registerAccount, onSuccess: () => refetch() });

    // #region handlers

    const form = useForm<z.infer<typeof registerFormStateSchema>>({
        validate: {
            didConsentToPrivacyPolicy: (it) => (!it ? "You must consent to the privacy policy" : null),
            didConsentToEula: (it) => (!it ? "You must consent to the EULA" : null),
        },
        initialValues: {
            didConsentToPrivacyPolicy: false,
            didConsentToEula: false,
        },
        mode: "uncontrolled",
    });

    function handleRegister() {
        const { didConsentToEula, didConsentToPrivacyPolicy } = form.getValues();
        if (form.validate().hasErrors || !user?.name || !user?.language || !didConsentToEula || !didConsentToPrivacyPolicy) {
            notify.show.error({ message: "Invalid values for 1 or more fields" });
            return;
        }

        register
            .mutateAsync({ userId: user.id, name: user.name, language: user.language })
            .then(() => notify.show.success({ message: "Your Account has been Registered!" }))
            .then(() => qk.invalidate("user", "id", user.id))
            .then(() => nav({ to: "/" }))
            .catch(notify.catch);
    }

    // #endregion

    const theme = useMantineTheme();

    return (
        <Content maw={theme.breakpoints.sm}>
            <Content.Heading>
                <Title order={2} pt="xl">
                    Complete Account Set Up
                </Title>
            </Content.Heading>
            <Accordion
                multiple
                defaultValue={["Profile Information", "Emails", "Text Numbers", "Call Numbers", "Locations", "Topic Subscriptions"]}
            >
                <Stack>
                    <AccordionItemWrapper title="Profile Information">
                        <ProfileContainer />
                    </AccordionItemWrapper>
                    <AccordionItemWrapper title="Emails">
                        <EmailEuds />
                    </AccordionItemWrapper>
                    <AccordionItemWrapper title="Text Numbers">
                        <TextEuds />
                    </AccordionItemWrapper>
                    <AccordionItemWrapper title="Call Numbers">
                        <VoiceEuds />
                    </AccordionItemWrapper>
                    <AccordionItemWrapper title="Locations" subtitle="Max 5 Addresses">
                        <LocationsContainer />
                    </AccordionItemWrapper>
                    {hasTopics && (
                        <AccordionItemWrapper title="Topic Subscriptions">
                            <Text>Subscribe to topics to receive messages regarding said topics.</Text>
                            <TopicsContainer />
                            <Text pt="sm">Go to your profile to update after registration.</Text>
                        </AccordionItemWrapper>
                    )}
                    {(user?.groups.length ?? 0) > 0 && <AccordionItemWrapper title="Groups">{<GroupsContainer />}</AccordionItemWrapper>}
                    <PaperWrapper>
                        <Checkbox
                            label={
                                <Text>
                                    By checking this box, you agree to receive SMS/text messages, phone calls, and/or emails from{" "}
                                    <strong>{customerData.name}</strong>, sent by Bryx, Inc. on their behalf, for the purpose of delivering
                                    emergency alerts, service notifications, and important announcements. Message frequency may vary.
                                    Message and data rates may apply. You can opt out of SMS messages at any time by replying STOP. For
                                    help, reply HELP. For more details, please review our{" "}
                                    <Anchor href="https://bryx.com/privacy-policy-1" c="primary" target="_blank">
                                        Privacy Policy
                                    </Anchor>
                                    .
                                </Text>
                            }
                            {...form.getInputProps("didConsentToPrivacyPolicy", { type: "checkbox" })}
                        />
                        <Checkbox
                            label={
                                <Text>
                                    By creating this account you agree to the{" "}
                                    <Anchor href="/account/service-agreement" c="primary" target="_blank">
                                        Service Agreement
                                    </Anchor>{" "}
                                    which governs both the Terms of Service and End User License Agreement.
                                </Text>
                            }
                            {...form.getInputProps("didConsentToEula", { type: "checkbox" })}
                        />
                        <Button onClick={handleRegister} mt="sm" loading={register.status != "idle"} disabled={register.status != "idle"}>
                            Register
                        </Button>
                    </PaperWrapper>
                </Stack>
            </Accordion>
        </Content>
    );
};

const AccordionItemWrapper = ({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) => {
    return (
        <Accordion.Item value={title} bd="none">
            <Content px={0}>
                <Accordion.Control px={0} styles={{ control: { borderRadius: "8px" }, chevron: { margin: "0 8px" } }}>
                    <Title order={3} id={title.replaceAll(" ", "")} px="xs" fz={20}>
                        {title}
                    </Title>
                    {!!subtitle && (
                        <Title order={5} px="xs">
                            {subtitle}
                        </Title>
                    )}
                </Accordion.Control>
                <Accordion.Panel styles={{ content: { padding: 0 } }}>
                    <PaperWrapper>{children}</PaperWrapper>
                </Accordion.Panel>
            </Content>
        </Accordion.Item>
    );
};

const queries = {
    customer: queryOptions({
        queryKey: ["register", "customer"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query AccountRegisterCustomer {
                        customer {
                            name
                        }
                    }
                `),
                {},
            ),
        select: to("customer"),
    }),
    euds: (params: { userId: string }) =>
        queryOptions({
            queryKey: ["register", "euds"],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query AccountRegisterEUDs($userId: UUID!) {
                            getUserEUDs(userId: $userId) {
                                id
                                name
                                value
                                type
                                isDefault
                            }
                        }
                    `),
                    params,
                ),
            select: to("getUserEUDs"),
        }),
};

const mutations = {
    registerAccount: graphql(`
        mutation RegisterAccount($userId: UUID!, $name: String!, $language: Language!) {
            updateUser(userId: $userId, params: { name: $name, language: $language }) {
                id
            }
            onboardUser(id: $userId) {
                id
            }
        }
    `),
    subscribeToTopic: graphql(`
        mutation RegisterAddUserToTopic($userId: UUID!, $topicId: UUID!) {
            addUserToTopic(userId: $userId, topicId: $topicId) {
                id
            }
        }
    `),
    unsubscribeFromTopic: graphql(`
        mutation RegisterRemoveUserFromTopic($userId: UUID!, $topicId: UUID!) {
            removeUserFromTopic(userId: $userId, topicId: $topicId) {
                id
            }
        }
    `),
    createEud: graphql(`
        mutation RegisterCreateEud($userId: UUID!, $name: String, $type: CM!, $value: String!) {
            addEUD(userId: $userId, eudParams: { name: $name, type: $type, value: $value }) {
                id
            }
        }
    `),
    updateAddress: graphql(`
        mutation RegisterUpdateAddress($userId: UUID!, $addressId: UUID!, $updates: UserAddressInput!) {
            updateUserAddress(userId: $userId, addressId: $addressId, address: $updates) {
                id
            }
        }
    `),
    deleteAddress: graphql(`
        mutation RegisterDeleteAddress($userId: UUID!, $addressId: UUID!) {
            removeUserAddress(userId: $userId, addressId: $addressId)
        }
    `),
    deleteEud: graphql(`
        mutation RegisterDeleteEud($userId: UUID!, $eudId: UUID!) {
            removeEUD(userId: $userId, eudId: $eudId)
        }
    `),
    verifyEud: graphql(`
        mutation RegisterVerifyEud($userId: UUID!, $eudId: UUID!, $code: String!) {
            verifyEUD(userId: $userId, eudId: $eudId, verificationCode: $code) {
                id
            }
        }
    `),
    resendEudVerificationCode: graphql(`
        mutation RegisterResendVerificationCode($userId: UUID!, $eudId: UUID!) {
            resendEUDVerificationCode(userId: $userId, eudId: $eudId) {
                id
            }
        }
    `),
    setIsEudInformational: graphql(`
        mutation RegisterMarkEudAsInformational($userId: UUID!, $eudId: UUID!, $isInformational: Boolean!) {
            setDefaultEud(userId: $userId, eudId: $eudId, isDefault: $isInformational) {
                id
            }
        }
    `),
    createAddress: graphql(`
        mutation RegisterCreateAddress(
            $userId: UUID!
            $name: String!
            $city: String!
            $state: String!
            $street: String!
            $street2: String
            $zip: String!
        ) {
            addUserAddress(
                userId: $userId
                address: { name: $name, city: $city, state: $state, street: $street, street2: $street2, zip: $zip }
            ) {
                id
            }
        }
    `),
};

/** @public */
export const Route = createFileRoute("/account/register")({
    beforeLoad: async ({ context }) => {
        const auth = await context.auth;
        if (!auth.isAuthenticated) throw redirect({ to: "/login", search: { redirect: location.href } });
        const data = await queryClient.ensureQueryData({
            ...userQueryOptions({ userId: getUserId(auth), isSupport: isSupport(auth) }),
        });
        if (data?.user.status == UserStatus.Active) throw redirect({ to: "/" });
    },
    component: Page,
});
